import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import ProfileEditor, {
  CARRIER3,
  DIVERSE_DIVERSE,
  DIVERSE_MAIN,
  MAIN_DIVERSE,
} from 'src/components/profilechart/ProfileEditorModal';
import { useLinkKind } from 'src/pages/ptp/hooks';

function getHeights(
  values: [number | string, number | string],
  formStates: [any, any],
  heightUnits: 'm' | 'ft'
): [number, number] {
  let result: number[] = [];
  if (values?.length !== 2) {
    return [0, 0];
  }

  for (let i = 0; i < values?.length; i++) {
    const value = values[i];
    let height: number;
    if (typeof value === 'string') {
      if (value.length === 0) {
        height = 0;
      } else {
        height = parseFloat(value);
      }
    } else {
      height = value;
    }

    result.push(height);
  }

  return result as [number, number];
}

function PTPProfileChart(props: any) {
  const { getValues, formState, getFieldState } = useFormContext();
  const prefs = useSelector((state: any) => state.mainFrame.prefs);
  const { lk, diverseHeights, selectedPathOptions } = useLinkKind();
  const [selectedPath, setSelectedPath] = useState(0);

  const {
    profile,
    path,
    reflectionLine,
    setModified,
    parentFormSubmitHandler,
    modified,
  } = props;

  let localRadioNumber = 0;
  let remoteRadioNumber = 0;
  if (lk === 'sd_1plus0') {
    // 1+0 SD uses a single radio — always use radio 0 for frequency
    localRadioNumber = 0;
    remoteRadioNumber = 0;
  } else if (selectedPath === MAIN_DIVERSE) {
    remoteRadioNumber = 1;
  } else if (selectedPath === DIVERSE_MAIN) {
    localRadioNumber = 1;
  } else if (selectedPath === DIVERSE_DIVERSE) {
    localRadioNumber = 1;
    remoteRadioNumber = 1;
  } else if (selectedPath === CARRIER3) {
    localRadioNumber = 2;
    remoteRadioNumber = 2;
  }

  const [lh, rh] = getHeights(
    getValues([
      `local.radios.${localRadioNumber}.antennas.0.height`,
      `remote.radios.${remoteRadioNumber}.antennas.0.height`,
    ]),
    [
      getFieldState(
        `local.radios.${localRadioNumber}.antennas.0.height`,
        formState
      ),
      getFieldState(
        `remote.radios.${remoteRadioNumber}.antennas.0.height`,
        formState
      ),
    ],
    prefs.heightUnits
  );

  let localHeight;
  let remoteHeight;

  if (diverseHeights) {
    if (selectedPath === MAIN_DIVERSE) {
      localHeight = diverseHeights[0][0];
      remoteHeight = diverseHeights[1][1];
    } else if (selectedPath === DIVERSE_MAIN) {
      localHeight = diverseHeights[0][1];
      remoteHeight = diverseHeights[1][0];
    } else if (selectedPath === DIVERSE_DIVERSE) {
      localHeight = diverseHeights[0][1];
      remoteHeight = diverseHeights[1][1];
    } else {
      localHeight = lh;
      remoteHeight = rh;
    }
  } else {
    localHeight = lh;
    remoteHeight = rh;
  }

  const freqMHz = Math.min(
    getValues(`local.radios[${localRadioNumber}].frequency.frequency_mhz`),
    getValues(`local.radios[${remoteRadioNumber}].frequency.frequency_mhz`)
  );
  const childProps = {
    accordionName: 'ptp_profile_panel',
    profileData: profile,
    localHeight,
    remoteHeight,
    path,
    frequencyGHz: freqMHz / 1000,
    loading: false,
    rangeUnits: prefs.rangeUnits,
    heightUnits: prefs.heightUnits,
    localSiteName: path.local.site.name,
    remoteSiteName: path.remote.site.name,
    reflectionLine,
    setModified,
    kind: 'ptp',
    parentFormSubmitHandler,
    modified,
    lk,
    selectedPath,
    setSelectedPath,
    selectedPathOptions,
  };

  return <ProfileEditor {...childProps} />;
}

export default injectIntl(PTPProfileChart);
